// JobDetailPanel: animated open/close for the job detail side panel.
//
// Reads persisted width from localStorage ("job_detail_w") and applies it
// on open so the panel re-opens at whatever width the user last dragged it to.
// Open/close uses a 3D perspective rotation mirroring the code-browser sidebar.
//
// During animation, direct children get a fixed width so content clips rather
// than reflowing. The fixed width is removed when the panel is fully open so
// PaneDivider drag-resize works normally.
//
// The CSS rule `job-detail { flex: 0 0 0px; … }` keeps the panel collapsed
// until this hook runs, preventing a flash of unstyled content.

import gsap from "gsap";
import type { LiveViewHook, CiPushEventWire } from "../types";
import { clearGsapResiduals } from "../types";
import { computeOpenTransform, computeCloseTransform } from "./panel-transform";
import * as Storage from "../../local-storage";

const PERSIST_KEY = "jobDetailW" as const;
const DEFAULT_W = 420;
const MIN_W = 400;
const DURATION = 0.5;

const persistedWidth = (): number => {
  const raw = Storage.getItem(PERSIST_KEY);
  if (!raw) return DEFAULT_W;
  const w = parseInt(raw, 10);
  return w >= MIN_W ? w : DEFAULT_W;
};

const applyCollapsed = (el: HTMLElement): void => {
  el.style.flex = "0 0 0px";
  el.style.minWidth = "0";
  el.style.overflow = "hidden";
};

// Lock each direct child's width so content clips instead of reflowing.
const freezeChildren = (el: HTMLElement, w: number): void => {
  for (const child of Array.from(el.children) as HTMLElement[]) {
    child.style.width = `${w}px`;
    child.style.minWidth = `${w}px`;
    child.style.flexShrink = "0";
  }
};

// Release children so PaneDivider resize works.
const thawChildren = (el: HTMLElement): void => {
  for (const child of Array.from(el.children) as HTMLElement[]) {
    child.style.width = "";
    child.style.minWidth = "";
    child.style.flexShrink = "";
  }
};

const dispatchDagResizeIntent = (
  panel: HTMLElement,
  panelTargetWidth: number,
  closing: boolean,
): void => {
  const layout = panel.closest<HTMLElement>(".pipeline-layout");
  const dag = layout?.querySelector<HTMLElement>("#pipeline-dag");
  if (!layout || !dag) return;

  const layoutWidth = layout.getBoundingClientRect().width;
  const divider =
    panel.previousElementSibling instanceof HTMLElement
      ? panel.previousElementSibling
      : null;
  const dividerWidth = divider?.getBoundingClientRect().width ?? 0;
  const targetWidth = Math.max(
    0,
    closing ? layoutWidth : layoutWidth - dividerWidth - panelTargetWidth,
  );

  dag.dispatchEvent(
    new CustomEvent("pipeline:dag-resize-intent", {
      detail: {
        width: targetWidth,
        duration: DURATION,
        ease: closing ? "power2.inOut" : "power2.out",
      },
    }),
  );
};

interface JobDetailPanelHook extends LiveViewHook<CiPushEventWire> {
  _wasOpen: boolean;
  _animating: boolean;
}

export const JobDetailPanel = {
  mounted(this: JobDetailPanelHook) {
    const panel = this.el;
    const isOpen = panel.dataset["open"] != null;
    this._wasOpen = isOpen;
    this._animating = false;

    if (isOpen) {
      applyCollapsed(panel);
      this._animating = true;
      dispatchDagResizeIntent(panel, persistedWidth(), false);
      animateOpen(panel, persistedWidth(), () => {
        this._animating = false;
      });
    }

    // Intercept the close button — animate out, THEN tell the server.
    panel.addEventListener("click", (e: Event) => {
      const target =
        e.target instanceof Element
          ? e.target.closest("[data-close-job-detail]")
          : null;
      if (!target || this._animating) return;
      e.preventDefault();
      e.stopPropagation();
      this._animating = true;
      dispatchDagResizeIntent(panel, 0, true);
      animateClose(panel, () => {
        this._animating = false;
        this._wasOpen = false;
        this.pushEvent("jobDeselect", {});
      });
    });
  },

  updated(this: JobDetailPanelHook) {
    const panel = this.el;
    const isOpen = panel.dataset["open"] != null;

    if (isOpen && !this._wasOpen && !this._animating) {
      applyCollapsed(panel);
      this._animating = true;
      dispatchDagResizeIntent(panel, persistedWidth(), false);
      animateOpen(panel, persistedWidth(), () => {
        this._animating = false;
      });
      this._wasOpen = true;
    } else if (isOpen && this._wasOpen && !this._animating) {
      // Content update while open — only restore if LiveView clobbered
      // our inline style. Read the live width; don't override PaneDivider.
      const liveW = panel.getBoundingClientRect().width;
      if (liveW < 10) {
        panel.style.flex = `0 0 ${persistedWidth()}px`;
      }
    } else if (!isOpen && this._wasOpen) {
      applyCollapsed(panel);
      this._wasOpen = false;
    }
  },
};

// --- Animations ---

const PERSPECTIVE = 4000;
const ORIGIN = "right center";

const apply3d = (el: HTMLElement, rotY: number, opacity: number): void => {
  el.style.transform =
    rotY === 0 ? "" : `perspective(${PERSPECTIVE}px) rotateY(${rotY}deg)`;
  el.style.transformOrigin = rotY === 0 ? "" : ORIGIN;
  el.style.opacity = opacity >= 1 ? "" : String(opacity);
};

const animateOpen = (
  panel: HTMLElement,
  targetWidth: number,
  onDone?: () => void,
): void => {
  panel.style.overflow = "hidden";
  panel.style.minWidth = "0";
  freezeChildren(panel, targetWidth);

  const proxy = { t: 0 };
  gsap.to(proxy, {
    t: 1,
    duration: DURATION,
    ease: "power2.out",
    onUpdate: () => {
      const t = proxy.t;
      panel.style.flex = `0 0 ${t * targetWidth}px`;
      // 3D completes by the midpoint — curve lives in panel-transform.
      const tr = computeOpenTransform(t);
      apply3d(panel, tr.rotY, tr.opacity);
    },
    onComplete: () => {
      // Keep flex-basis at target so CSS default (0px) doesn't collapse us.
      // PaneDivider overrides this inline value when dragging.
      panel.style.flex = `0 0 ${targetWidth}px`;
      panel.style.overflow = "";
      panel.style.minWidth = "";
      apply3d(panel, 0, 1);
      clearGsapResiduals(panel);
      thawChildren(panel);
      onDone?.();
    },
  });
};

const animateClose = (panel: HTMLElement, onDone: () => void): void => {
  const startWidth = panel.getBoundingClientRect().width;
  panel.style.overflow = "hidden";
  panel.style.flex = `0 0 ${startWidth}px`;
  freezeChildren(panel, startWidth);

  const proxy = { t: 0 };
  gsap.to(proxy, {
    t: 1,
    duration: DURATION,
    ease: "power2.inOut",
    onUpdate: () => {
      const t = proxy.t;
      panel.style.flex = `0 0 ${(1 - t) * startWidth}px`;
      // 3D starts at the midpoint — curve lives in panel-transform.
      const tr = computeCloseTransform(t);
      apply3d(panel, tr.rotY, tr.opacity);
    },
    onComplete: () => {
      applyCollapsed(panel);
      apply3d(panel, 0, 1);
      clearGsapResiduals(panel);
      thawChildren(panel);
      onDone();
    },
  });
};
